import { useState } from "react";
import { X, ChevronRight, Clock, AlertTriangle, Ban, FileWarning, PhoneOff, HelpCircle } from "lucide-react";

const personas = [
  {
    id: "admin",
    label: "THE ADMINISTRATOR",
    tagline: "Drowning in paperwork. Zero visibility.",
    color: "bg-rose-500",
    blobClass: "blob-pink",
    pains: [
      {
        icon: Clock,
        headline: "80+ hours/month on manual data entry",
        story: "Student records in exercise books. Attendance in registers. Fee payments on loose sheets. You spend more time on paperwork than actually running your school.",
      },
      {
        icon: AlertTriangle,
        headline: "No idea who has paid and who hasn't",
        story: "Parents claim they've paid. Receipts go missing. The accountant's ledger doesn't match the bursar's records. End of term is always chaos.",
      },
      {
        icon: Ban,
        headline: "GES reports take weeks to compile",
        story: "Every time the district office asks for enrollment data or performance stats, your staff drops everything. Reports that should take minutes take weeks.",
      },
    ],
  },
  {
    id: "teacher",
    label: "THE TEACHER",
    tagline: "Overworked. Under-supported. Alone.",
    color: "bg-blue-500",
    blobClass: "blob-blue",
    pains: [
      {
        icon: FileWarning,
        headline: "Report cards calculated by hand — every term",
        story: "Calculating cumulative averages, rankings, and remarks for 200+ students with a calculator and pen. One mistake means starting the entire class over.",
      },
      {
        icon: Clock,
        headline: "Zero tools for tracking student progress",
        story: "You know Kofi is struggling, but you have no data to show when it started, what subjects are affected, or how to intervene. You're teaching blind.",
      },
      {
        icon: HelpCircle,
        headline: "No way to communicate with parents",
        story: "Parent-teacher meetings are the only touchpoint. If a student is absent for a week, you have no quick way to reach the parent. Problems escalate silently.",
      },
    ],
  },
  {
    id: "parent",
    label: "THE PARENT",
    tagline: "Paying fees. Getting nothing back.",
    color: "bg-amber-500",
    blobClass: "blob-amber",
    pains: [
      {
        icon: PhoneOff,
        headline: "Complete blackout on your child's progress",
        story: "You pay fees every term but have zero visibility into what's happening at school. Is your child attending? Passing? Struggling? You only find out at report card time.",
      },
      {
        icon: AlertTriangle,
        headline: "Fee payment is a nightmare",
        story: "Queue at the bank. Get a receipt. Bring it to school. Wait for the bursar to verify. Lose the receipt and you might as well have not paid. The whole process is broken.",
      },
      {
        icon: Ban,
        headline: "No trust in the system",
        story: "Schools lose records. Grades appear wrong. Nobody can explain where your money went. You're investing in your child's future with no accountability.",
      },
    ],
  },
];

function PainCard({ pain, index, isOpen, onClick }) {
  const Icon = pain.icon;
  return (
    <div
      data-testid={`pain-card-${index}`}
      onClick={onClick}
      className={`cursor-pointer border-2 transition-all duration-300 ${
        isOpen
          ? "border-[#0A0A0A] bg-[#0A0A0A] text-white"
          : "border-gray-200 bg-white hover:border-gray-400"
      }`}
    >
      <div className="p-6 flex items-start gap-4">
        <div className={`w-10 h-10 flex-shrink-0 flex items-center justify-center ${
          isOpen ? "bg-white/10" : "bg-gray-100"
        }`}>
          <Icon size={20} strokeWidth={2.5} className={isOpen ? "text-white" : "text-[#0A0A0A]"} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <h4
              className={`text-sm font-black uppercase tracking-tight leading-tight ${
                isOpen ? "text-white" : "text-[#0A0A0A]"
              }`}
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            >
              {pain.headline}
            </h4>
            <ChevronRight
              size={16}
              strokeWidth={3}
              className={`flex-shrink-0 transition-transform duration-300 ${
                isOpen ? "rotate-90 text-white/60" : "text-gray-400"
              }`}
            />
          </div>
          {isOpen && (
            <p
              className="text-sm text-white/70 mt-3 leading-relaxed font-medium"
              style={{ fontFamily: "'Satoshi', sans-serif" }}
            >
              {pain.story}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default function PainPoints() {
  const [activePersona, setActivePersona] = useState("admin");
  const [openPain, setOpenPain] = useState(0);

  const current = personas.find((p) => p.id === activePersona);

  return (
    <section data-testid="pain-points-section" className="relative bg-white py-24 lg:py-32 overflow-hidden">
      <div className="blob-pink" style={{ top: '-15%', right: '-10%' }} />
      <div className="blob-blue" style={{ bottom: '-10%', left: '-8%' }} />
      <div className="blob-amber" style={{ top: '40%', left: '60%' }} />

      <div className="relative z-10 max-w-6xl mx-auto px-6">
        {/* Section header */}
        <div className="text-center mb-6">
          <span
            className="inline-flex items-center border-2 border-[#0A0A0A] px-5 py-2 text-xs uppercase tracking-[0.15em] text-[#0A0A0A] font-black bg-white mb-8"
            style={{ fontFamily: "'Satoshi', sans-serif" }}
          >
            The Problem
          </span>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-tight text-[#0A0A0A] mb-4 leading-[0.95]"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            data-testid="pain-points-heading"
          >
            Ghana's Schools Deserve
            <br />
            <span className="text-gray-300">Better Than This</span>
          </h2>
          <p
            className="text-base text-gray-500 max-w-2xl mx-auto font-medium mt-4"
            style={{ fontFamily: "'Satoshi', sans-serif" }}
          >
            Every day, thousands of administrators, teachers, and parents across Ghana
            struggle with broken, outdated systems. These are their stories.
          </p>
        </div>

        {/* Persona Switcher */}
        <div className="flex flex-col sm:flex-row justify-center gap-3 mb-12 mt-12">
          {personas.map((persona) => (
            <button
              key={persona.id}
              data-testid={`persona-tab-${persona.id}`}
              onClick={() => {
                setActivePersona(persona.id);
                setOpenPain(0);
              }}
              className={`px-6 py-3 text-xs font-black uppercase tracking-wider transition-all duration-200 border-2 ${
                activePersona === persona.id
                  ? "bg-[#0A0A0A] text-white border-[#0A0A0A]"
                  : "bg-white text-gray-500 border-gray-200 hover:border-gray-400 hover:text-[#0A0A0A]"
              }`}
              style={{ fontFamily: "'Satoshi', sans-serif" }}
            >
              {persona.label}
            </button>
          ))}
        </div>

        {/* Content Area */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left: Big statement */}
          <div className="sticky top-32">
            <div className={`w-3 h-16 ${current.color} mb-6`} />
            <h3
              className="text-2xl sm:text-3xl lg:text-4xl font-black uppercase tracking-tight text-[#0A0A0A] leading-[0.95] mb-4"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
              data-testid="persona-tagline"
            >
              {current.tagline}
            </h3>
            <p
              className="text-gray-500 text-sm font-medium leading-relaxed max-w-sm"
              style={{ fontFamily: "'Satoshi', sans-serif" }}
            >
              Click each pain point to reveal the full story.
              These aren't hypotheticals — this is happening in schools right now.
            </p>

            {/* Stat callout */}
            <div className="mt-10 p-6 bg-gray-50 border-2 border-gray-100">
              <div
                className="text-5xl font-black text-[#0A0A0A] mb-1"
                style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
              >
                {activePersona === "admin" && "80%"}
                {activePersona === "teacher" && "3 weeks"}
                {activePersona === "parent" && "0"}
              </div>
              <div
                className="text-sm text-gray-500 font-bold uppercase tracking-wider"
                style={{ fontFamily: "'Satoshi', sans-serif" }}
              >
                {activePersona === "admin" && "of admin time wasted on manual tasks"}
                {activePersona === "teacher" && "spent on report cards each term"}
                {activePersona === "parent" && "visibility into their child's daily progress"}
              </div>
            </div>
          </div>

          {/* Right: Pain cards */}
          <div className="space-y-3">
            {current.pains.map((pain, i) => (
              <PainCard
                key={`${current.id}-${i}`}
                pain={pain}
                index={i}
                isOpen={openPain === i}
                onClick={() => setOpenPain(openPain === i ? -1 : i)}
              />
            ))}

            {/* Solution teaser */}
            <div className="mt-8 p-6 border-2 border-dashed border-gray-200 text-center">
              <p
                className="text-sm text-gray-400 font-bold uppercase tracking-wider mb-3"
                style={{ fontFamily: "'Satoshi', sans-serif" }}
              >
                There is a better way
              </p>
              <a
                href="#features"
                data-testid="pain-points-cta"
                className="inline-flex items-center gap-2 bg-[#0A0A0A] text-white font-black px-8 py-3 text-sm uppercase tracking-wider transition-all duration-200 hover:bg-[#1a1a1a] group"
                style={{ fontFamily: "'Satoshi', sans-serif" }}
              >
                See How VelTech Solves This
                <ChevronRight size={16} strokeWidth={3} className="group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
